"use client";

import { useState } from "react";
import {
  Button,
  Icon,
  Input,
  Label,
  Popover,
  PopoverTrigger,
  Text,
} from "@focal/ui-kit";
import { Participant, SuggestedParticipants } from "@/types";
import {
  ConversationPersonPopoverPaginated,
  UpdateParticipantData,
} from "@/components/pages/Conversations/ConversationsDetails/ConversationPersonTag";
import { cn } from "@/lib/utils";
import { ParticipantChip } from "./ParticipantChip";

// Collapse to a single row (product decision 2026-08-25, overrides the
// Figma annotation's above-4 threshold)
const COLLAPSED_VISIBLE_COUNT = 2;

interface MeetingDetailsFieldsProps {
  title: string;
  onTitleChange: (title: string) => void;
  titlePlaceholder: string;
  participants: Partial<Participant>[];
  onAddParticipant: (data: UpdateParticipantData) => void;
  onRemoveParticipant: (participantId: string) => void;
  suggestedParticipants?: SuggestedParticipants;
  disabled?: boolean;
}

export const MeetingDetailsFields = ({
  title,
  onTitleChange,
  titlePlaceholder,
  participants,
  onAddParticipant,
  onRemoveParticipant,
  suggestedParticipants,
  disabled,
}: MeetingDetailsFieldsProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const shouldCollapse = participants.length > COLLAPSED_VISIBLE_COUNT;
  const overflowCount = participants.length - COLLAPSED_VISIBLE_COUNT;
  const visibleParticipants =
    shouldCollapse && !expanded
      ? participants.slice(0, COLLAPSED_VISIBLE_COUNT)
      : participants;

  return (
    <div
      className="w-full text-left flex flex-col gap-4 border-l border-blue-400 pl-4"
      data-cy="meeting-details-fields"
    >
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label htmlFor="pre-meeting-title">Meeting title</Label>
          <Text variant="p3" textColor="muted">
            Optional
          </Text>
        </div>
        <Input
          id="pre-meeting-title"
          value={title}
          placeholder={titlePlaceholder}
          disabled={disabled}
          onChange={(e) => onTitleChange(e.target.value)}
          data-cy="pre-meeting-title-input"
        />
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <Label>Participants</Label>
          <Text variant="p3" textColor="muted">
            Optional
          </Text>
        </div>
        <div
          className="bg-background-tone rounded-lg p-2 flex flex-col gap-4"
          data-cy="pre-meeting-participants-panel"
        >
          {participants.length > 0 && (
            <div className="flex items-start gap-1">
              <div
                className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-1 flex-1 min-w-0"
                data-cy="pre-meeting-participants-list"
              >
                {visibleParticipants.map((p) => (
                  <ParticipantChip
                    key={p.id ?? p.email ?? p.name}
                    participant={p}
                    onRemove={onRemoveParticipant}
                    disabled={disabled}
                  />
                ))}
              </div>
              {shouldCollapse && (
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    aria-label={`Show ${overflowCount} more participants`}
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background text-base text-typography-primary",
                      expanded && "invisible",
                    )}
                    onClick={() => setExpanded(true)}
                    data-cy="pre-meeting-participants-overflow"
                  >
                    +{overflowCount}
                  </button>
                  <Button
                    type="button"
                    variant="ghostSecondary"
                    size="iconSm"
                    aria-label={
                      expanded ? "Collapse participants" : "Expand participants"
                    }
                    onClick={() => setExpanded((v) => !v)}
                    data-cy="pre-meeting-participants-toggle"
                  >
                    <Icon name="UnfoldMoreIcon" />
                  </Button>
                </div>
              )}
            </div>
          )}
          <Popover open={popoverOpen} onOpenChange={setPopoverOpen} modal>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={disabled}
                iconLeft={<Icon name="AddIcon" />}
                className="self-start"
                data-cy="pre-meeting-add-participant"
              >
                Add participant
              </Button>
            </PopoverTrigger>
            <ConversationPersonPopoverPaginated
              onSelectParticipant={onAddParticipant}
              onCloseModal={() => setPopoverOpen(false)}
              open={popoverOpen}
              participants={participants}
              suggestedParticipants={suggestedParticipants}
              suggestedHeading="Suggested match"
              suggestedMaxVisible={3}
              createClientLabel="Add a client"
              placeholder="Search"
            />
          </Popover>
        </div>
      </div>
    </div>
  );
};
